/**
 * Medlemssider: personlig forside, «Mine hold», holddetalje og
 * afvisningssiden.
 */

import { useState } from 'react';
import {
  antal,
  api,
  departmentLabel,
  formatDateTime,
  formatTime,
  relativeDay,
  scheduleText,
  shortName,
} from '../lib/api.js';
import { Link } from '../lib/router.js';
import {
  ActionButton,
  AsyncSection,
  Badge,
  departmentBadgeKind,
  ChipFilter,
  EmptyState,
  Meta,
  Notice,
  PageHead,
  SectionHead,
  useAction,
  useAsync,
} from '../components/ui.js';
import { Icon, IconBadge, departmentIcon, type IconName } from '../components/icons.js';

/* ------------------------------------------------------------------ */
/* Typer fra API'et                                                    */
/* ------------------------------------------------------------------ */

interface FamilyMember {
  memberId: string;
  personKey: string;
  name: string;
  relation: 'self' | 'child';
}

interface InboxItem {
  id: string;
  title: string;
  body: string;
  audienceLabel: string;
  priority: 'normal' | 'important';
  publishedAt: string;
  senderName: string;
  read: boolean;
}

interface HomeEvent {
  id: string;
  title: string;
  location: string;
  startsAt: string;
  allDay: boolean;
  audienceLabel: string;
  status: 'scheduled' | 'cancelled';
  helpersNeeded: number;
}

interface CalendarEntry {
  sessionId: string;
  date: string;
  startsAt: string;
  endsAt: string;
  status: 'scheduled' | 'cancelled' | 'moved' | 'closed';
  reason: string;
  isExtra: boolean;
  teamId: string;
  teamName: string;
  teamSlug: string;
  department: string;
  location: string;
  personKey: string;
  personName: string;
  absence: { id: string; reason: string; reasonLabel: string; note: string } | null;
}

interface HomeData {
  greeting: string;
  name: string;
  today: string;
  latestAnnouncement: InboxItem | null;
  unreadCount: number;
  nextActivities: CalendarEntry[];
  nextEvents: HomeEvent[];
  family: FamilyMember[];
  volunteers: {
    openShifts: number;
    openSlots: number;
    mySignups: number;
    nextShift: { id: string; title: string; startsAt: string; capacity: number; signedUp: number } | null;
  };
  hasWorkspace: boolean;
  pushAvailable: boolean;
  needsFamilyLink: boolean;
}

/* ------------------------------------------------------------------ */
/* Forside                                                             */
/* ------------------------------------------------------------------ */

/**
 * Skal aldersgruppen skrives under holdnavnet?
 *
 * Kun hvis den tilføjer noget. Navnene i Conventus bærer den tit selv
 * («Lillespring 0.-2. klasse»), og så er en linje mere bare en gentagelse.
 *
 * Sammenligningen er slap med vilje: bindestreg og tankestreg blandes i
 * kataloget, og mellemrum står forskelligt. Et strengt lig-med ville
 * overse næsten alle gentagelserne.
 */
export function visAldersgruppe(navn: string, aldersgruppe: string): boolean {
  if (!aldersgruppe.trim()) return false;
  const skrab = (t: string): string =>
    t.toLowerCase().replace(/[–—]/g, '-').replace(/[^a-z0-9æøå]/g, '');
  return !skrab(navn).includes(skrab(aldersgruppe));
}

/** Genvejene på forsiden. Rækkefølgen er den, de bruges i. */
const SHORTCUTS: Array<{ to: string; title: string; sub: string; icon: IconName }> = [
  { to: '/mine-hold', title: 'Mine hold', sub: 'Hold, tider og steder', icon: 'teams' },
  { to: '/kalender', title: 'Kalender', sub: 'Træninger og afbud', icon: 'calendar' },
  { to: '/beskeder', title: 'Beskeder', sub: 'Din indbakke', icon: 'message' },
  { to: '/profil', title: 'Profil', sub: 'Familie og notifikationer', icon: 'profile' },
];

/**
 * Næste træning som hero-kort.
 *
 * Det er DET, en forælder åbner appen for: skal barnet af sted i dag, og
 * hvornår. Kortet har derfor sin egen lyse røde flade, hvor resten af
 * forsiden er hvid — den ene røde flade på skærmen, og den sidder på det
 * ene, der haster.
 *
 * Datoen står øverst og størst. Så holdnavnet, så hvem det gælder. Den
 * rækkefølge er bevidst: klokkeslættet er det, man tjekker; navnet er det,
 * man allerede kender.
 */
function NextTrainingHero({ entry }: { entry: CalendarEntry }): JSX.Element {
  const off = entry.status === 'cancelled' || entry.status === 'closed';
  return (
    <article className={`hero-card${off ? ' hero-card--off' : ''}`}>
      {/*
        Bølgelinjen fra designguiden. Rent dekorativ, derfor aria-hidden og
        ingen titel — en skærmlæser skal ikke læse en streg op.
      */}
      <svg className="hero-card__wave" viewBox="0 0 320 48" aria-hidden="true" focusable="false">
        <path d="M0 30C60 6 110 44 168 26 226 8 274 30 320 14" fill="none"
          stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity="0.5" />
        <path d="M0 38C60 14 110 52 168 34 226 16 274 38 320 22" fill="none"
          stroke="var(--wave-second)" strokeWidth="2" strokeLinecap="round" opacity="0.85" />
      </svg>

      <p className="hero-card__when">
        <span className="hero-card__day">{relativeDay(entry.startsAt)}</span>
        <span className="hero-card__time">
          {formatTime(entry.startsAt)}–{formatTime(entry.endsAt)}
        </span>
      </p>
      <h3 className="hero-card__team">{entry.teamName}</h3>
      <p className="hero-card__who">{entry.personName}</p>

      <p className="hero-card__where">
        <Icon name="place" size={16} />
        {entry.location}
      </p>

      {entry.status === 'cancelled' ? (
        <p className="hero-card__flag">
          <Badge kind="warning" icon="absence">Aflyst</Badge>
          {entry.reason ? <span className="muted"> {entry.reason}</span> : null}
        </p>
      ) : entry.status === 'moved' ? (
        <p className="hero-card__flag">
          <Badge kind="warning" icon="clock">Flyttet</Badge>
          {entry.reason ? <span className="muted"> {entry.reason}</span> : null}
        </p>
      ) : entry.absence ? (
        <p className="hero-card__flag">
          <Badge kind="muted" icon="absence">Afbud: {entry.absence.reasonLabel}</Badge>
        </p>
      ) : (
        /*
          Afbud MELDES i kalenderen og ikke her. Formularen dér har
          årsagsvalg, note, kvittering og fejlhåndtering; en genpart af den
          på forsiden ville være to steder at få fejlbeskeder galt. Knappen
          fører derhen, hvor det sker.
        */
        <p className="hero-card__action">
          <Link className="btn btn--secondary btn--small" to="/kalender">
            <Icon name="absence" size={17} />
            Meld afbud
          </Link>
        </p>
      )}
    </article>
  );
}

export function HomePage(): JSX.Element {
  const state = useAsync<HomeData>(() => api.get('/api/home'));

  return (
    <div className="stack">
      <AsyncSection state={state} skeletonLines={5}>
        {(data) => (
          <>
            <PageHead
              /* Kontoen har ikke altid et navn: ved magic-link-login kender
                 appen kun mailadressen, indtil familien er forbundet. Uden
                 den her stod der «God aften,» og så ingenting. */
              title={
                data.name.trim()
                  ? `${data.greeting}, ${data.name.trim().split(' ')[0]}`
                  : data.greeting
              }
              sub={new Intl.DateTimeFormat('da-DK', {
                timeZone: 'Europe/Copenhagen',
                weekday: 'long',
                day: 'numeric',
                month: 'long',
              }).format(new Date(data.today))}
            />

            {/*
              TO FORSKELLIGE BESKEDER, OG HVORFOR DET IKKE ER PYNT.

              «Forbind din profil» var den eneste besked før. Den er rigtig for
              en forælder: hun HAR børn i foreningen, og forbindelsen finder
              dem. Men bestyrelsen og udvalgene har også konti — og flere af dem
              har ingen børn her. For dem er beskeden en opgave, der ikke kan
              løses: de trykker «Forbind nu», systemet finder ingenting, og de
              sidder tilbage med indtrykket af, at de gjorde noget forkert.

              Værre: forsiden, «Mine hold» og kalenderen siger alle tre det
              samme til dem. Tre af fem sider er dermed en påmindelse om noget,
              de ikke kan udføre — og det er DET indtryk, de danner sig af
              appen.

              Har kontoen adgang til administrationen, siger beskeden derfor
              noget andet: at forbindelsen kun er nødvendig, hvis man selv har
              børn her, og at foreningens hold ligger under Administration.
            */}
            {data.needsFamilyLink ? (
              <Notice kind="info">
                {data.hasWorkspace ? (
                  <>
                    <strong>Har du børn i foreningen?</strong>
                    <p className="small mt-1">
                      Så forbind din konto med medlemsregisteret — så står deres hold og
                      træninger her på forsiden. Har du ikke, er der ikke noget at
                      forbinde, og du kan roligt lade den ligge.
                    </p>
                    <p className="mt-3 row" style={{ gap: 8, flexWrap: 'wrap' }}>
                      <Link className="btn btn--small" to="/medlemslogin">
                        <Icon name="family" size={17} />
                        Forbind min profil
                      </Link>
                      <Link className="btn btn--secondary btn--small" to="/arbejdsrum/hold">
                        <Icon name="teams" size={17} />
                        Se foreningens hold
                      </Link>
                    </p>
                  </>
                ) : (
                  <>
                    <strong>Forbind din profil</strong>
                    <p className="small mt-1">
                      Du har endnu ikke forbundet din konto med foreningens medlemsregister.
                      Uden det ser du hverken hold, kalender eller holdbeskeder.
                    </p>
                    <p className="mt-3">
                      <Link className="btn btn--small" to="/medlemslogin">
                        <Icon name="family" size={17} />
                        Forbind nu
                      </Link>
                    </p>
                  </>
                )}
              </Notice>
            ) : null}

            {/*
              Næste træning står ØVERST. Det er det, en forælder åbner appen
              for at finde ud af — hvornår skal barnet af sted, og er der
              overhovedet træning i dag. Beskeder kom før her; de er
              vigtige, men de haster sjældnere end en aflysning i aften.

              Den FØRSTE træning får hero-kortet. De næste står som en kort
              liste under: de er til orientering, ikke til handling, og skal
              ikke fylde lige så meget som den, der er om lidt.
            */}
            <section className="section" aria-labelledby="naeste-traening">
              <SectionHead
                title="Næste træning"
                icon="calendar"
                id="naeste-traening"
                action={
                  data.nextActivities.length > 0 ? (
                    <Link className="section__link" to="/kalender">
                      Hele kalenderen
                    </Link>
                  ) : null
                }
              />
              {data.nextActivities.length === 0 ? (
                <div className="card">
                  <EmptyState title="Ingen kommende træninger" icon="calendar">
                    {data.needsFamilyLink
                      ? 'Forbind din profil for at se familiens træninger.'
                      : 'Der er ikke registreret kommende træninger på familiens hold.'}
                  </EmptyState>
                </div>
              ) : (
                <>
                  <NextTrainingHero entry={data.nextActivities[0]!} />
                  {data.nextActivities.length > 1 ? (
                    <ul className="upnext">
                      {data.nextActivities.slice(1).map((entry) => (
                        <li className="upnext__row" key={`${entry.sessionId}-${entry.personKey}`}>
                          <span className="upnext__when">
                            <strong className={entry.status === 'cancelled' ? 'strike' : undefined}>
                              {relativeDay(entry.startsAt)}
                            </strong>
                            <span className="muted">{formatTime(entry.startsAt)}</span>
                          </span>
                          <span className="upnext__what">
                            <span className="upnext__team">{entry.teamName}</span>
                            <span className="muted">{entry.personName}</span>
                          </span>
                          {entry.status === 'cancelled' ? (
                            <Badge kind="warning" icon="absence">Aflyst</Badge>
                          ) : entry.status === 'moved' ? (
                            <Badge kind="warning" icon="clock">Flyttet</Badge>
                          ) : entry.absence ? (
                            <Badge kind="muted" icon="absence">Afbud</Badge>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </>
              )}
            </section>

            {/*
              ARRANGEMENTER FÅR DERES EGET AFSNIT — de blandes IKKE ind i
              «Næste træning».

              Helten øverst svarer på ét spørgsmål: skal vi af sted i dag?
              Opvisningen om seks uger er et andet slags spørgsmål, og lagde
              vi den ind i samme liste, ville den enten skubbe den næste
              træning ned eller drukne mellem tirsdag og torsdag.

              Afsnittet står der kun, når der ER noget. En tom overskrift om
              foreningens liv er en dårlig reklame for foreningens liv.
            */}
            {data.nextEvents.length > 0 ? (
              <section className="section" aria-labelledby="arrangementer">
                <SectionHead
                  title="Sker der i foreningen"
                  icon="calendar"
                  id="arrangementer"
                  action={
                    <Link className="section__link" to="/kalender">
                      Hele kalenderen
                    </Link>
                  }
                />
                <ul className="upnext">
                  {data.nextEvents.map((event) => (
                    <li className="upnext__row" key={event.id}>
                      <span className="upnext__when">
                        <strong className={event.status === 'cancelled' ? 'strike' : undefined}>
                          {relativeDay(event.startsAt)}
                        </strong>
                        {event.allDay ? null : (
                          <span className="muted">{formatTime(event.startsAt)}</span>
                        )}
                      </span>
                      <span className="upnext__what">
                        <span className="upnext__team">{event.title}</span>
                        <span className="muted">
                          {event.location || event.audienceLabel}
                        </span>
                      </span>
                      {event.status === 'cancelled' ? (
                        <Badge kind="warning" icon="absence">Aflyst</Badge>
                      ) : event.helpersNeeded > 0 ? (
                        /* Opfordringen står dér, hvor arrangementet står.
                           Skal man først ind på en anden side for at opdage,
                           at der mangler hjælpere, opdager man det ikke. */
                        <Badge kind="muted" icon="volunteer">
                          {event.helpersNeeded} mangler
                        </Badge>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}

            {/*
              Genvejene står HER og ikke nederst. De er det, man bruger, når
              man ikke kom efter næste træning — og en genvej, man skal rulle
              forbi tre sektioner for at finde, er ikke en genvej.
            */}
            <nav className="section" aria-label="Genveje">
              <div className="card-grid card-grid--2">
                {SHORTCUTS.map((shortcut) => (
                  <Link key={shortcut.to} className="tile" to={shortcut.to}>
                    <IconBadge name={shortcut.icon} />
                    <span className="tile__text">
                      <span className="tile__title">{shortcut.title}</span>
                      <span className="tile__sub">
                        {shortcut.to === '/beskeder' && data.unreadCount > 0
                          ? antal(data.unreadCount, 'ulæst', 'ulæste')
                          : shortcut.sub}
                      </span>
                    </span>
                  </Link>
                ))}
              </div>
            </nav>

            <section className="section" aria-labelledby="seneste-besked">
              <SectionHead
                title="Beskeder"
                icon="message"
                id="seneste-besked"
                action={
                  <Link className="section__link" to="/beskeder">
                    {data.unreadCount > 0 ? antal(data.unreadCount, 'ulæst', 'ulæste') : 'Indbakken'}
                  </Link>
                }
              />
              {data.latestAnnouncement ? (
                <article className="card">
                  <div className="card__header">
                    <h3>{data.latestAnnouncement.title}</h3>
                    {data.latestAnnouncement.priority === 'important' ? (
                      <Badge kind="important">Vigtig</Badge>
                    ) : null}
                  </div>
                  <p className="small muted">
                    {data.latestAnnouncement.audienceLabel} ·{' '}
                    {relativeDay(data.latestAnnouncement.publishedAt)}
                  </p>
                  <p className="small mt-2">
                    {data.latestAnnouncement.body.slice(0, 180)}
                    {data.latestAnnouncement.body.length > 180 ? '…' : ''}
                  </p>
                  <p className="mt-3">
                    <Link className="btn btn--secondary btn--small" to="/beskeder">
                      Læs beskeden
                    </Link>
                  </p>
                </article>
              ) : (
                <div className="card">
                  <EmptyState title="Ingen beskeder endnu" icon="message">
                    Når foreningen, din afdeling eller dit hold sender en besked, står den her.
                  </EmptyState>
                </div>
              )}
            </section>

            <section className="section" aria-labelledby="frivillig">
              <SectionHead
                title="Frivillige opgaver"
                icon="volunteer"
                id="frivillig"
                action={
                  <Link className="section__link" to="/frivillige">
                    Se opgaver
                  </Link>
                }
              />
              <div className="card">
                {data.volunteers.nextShift ? (
                  <div className="meta">
                    <Meta icon="volunteer" label="Næste opgave">
                      <strong>{data.volunteers.nextShift.title}</strong>
                    </Meta>
                    <Meta icon="clock" label="Tidspunkt">
                      {formatDateTime(data.volunteers.nextShift.startsAt)}
                    </Meta>
                    <Meta icon="teams" label="Pladser">
                      {data.volunteers.nextShift.signedUp} af {data.volunteers.nextShift.capacity}{' '}
                      pladser er besat · {data.volunteers.openShifts} opgave(r) mangler stadig hjælp
                      {data.volunteers.mySignups > 0
                        ? ` · du er tilmeldt ${data.volunteers.mySignups}`
                        : ''}
                    </Meta>
                  </div>
                ) : (
                  <p className="small muted">Der er ingen kommende frivillige opgaver lige nu.</p>
                )}
              </div>
            </section>

            {/*
              Administration ligger nu i bundmenuen for de konti, der har
              adgang. En genvej her ville være det samme punkt to gange på
              samme skærm — og to veje til det samme sted gør det sværere,
              ikke lettere, at forstå, hvor man er.
            */}
          </>
        )}
      </AsyncSection>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Mine hold                                                           */
/* ------------------------------------------------------------------ */

interface MyTeam {
  teamId: string;
  slug: string;
  name: string;
  department: string;
  ageGroup: string;
  weekday: string;
  startTime: string;
  endTime: string;
  location: string;
  firstDate: string;
  dataReview: string | null;
  awaitingTrainer: boolean;
  members: Array<{ personKey: string; name: string }>;
}

/**
 * Hvorfor der ingen hold er — uden at påstå mere, end appen ved.
 *
 * Her stod «Der er ikke importeret hold for den valgte person endnu.»
 * Tre ting var galt med den sætning over for en forælder: «importeret» er
 * et ord fra administrationen, «endnu» lover at det retter sig af sig selv,
 * og «den valgte person» siger ikke hvem.
 *
 * Værre: appen KAN ikke se forskel på «barnet er ikke tilmeldt» og «holdet
 * er ikke med i appen». Tilknytninger findes kun for de hold, kataloget
 * kender, så begge tilfælde ser ens ud herinde. Så siger vi begge dele i
 * stedet for at gætte på den ene — og peger hen, hvor tilmelding faktisk
 * foregår.
 */
export function ingenHoldTekst(navn: string | null): string {
  const hvem = navn ? `et hold for ${navn}` : 'et hold for jer';
  return `Vi kan ikke se ${hvem} i denne sæson. Enten er der ikke tilmeldt et hold, eller også kender appen ikke holdet endnu. Tilmelding sker i Conventus, ikke her.`;
}

export function MyTeamsPage({ hasWorkspace = false }: { hasWorkspace?: boolean }): JSX.Element {
  const [person, setPerson] = useState('alle');
  const state = useAsync<{ teams: MyTeam[]; family: FamilyMember[] }>(() => api.get('/api/my-teams'));

  return (
    <div className="stack">
      <PageHead title="Mine hold" sub="Familiens hold i denne sæson" />
      <AsyncSection state={state}>
        {(data) => {
          const filtered =
            person === 'alle'
              ? data.teams
              : data.teams.filter((t) => t.members.some((m) => m.personKey === person));

          return (
            <>
              {data.family.length > 1 ? (
                <ChipFilter
                  label="Vis hold for"
                  value={person}
                  onChange={setPerson}
                  options={[
                    { value: 'alle', label: 'Alle' },
                    ...data.family.map((f) => ({
                      value: f.personKey,
                      label: shortName(f.name, data.family.map((x) => x.name)),
                    })),
                  ]}
                />
              ) : null}

              {filtered.length === 0 ? (
                <div className="card">
                  <EmptyState
                    title="Ingen hold at vise"
                    icon="teams"
                    action={
                      data.family.length === 0 ? (
                        /*
                          Har kontoen adgang til administrationen, er der en vej
                          videre, som ikke kræver en familie: foreningens egne
                          hold. Uden den er siden en blindgyde for netop de
                          brugere, der skal vurdere appen.
                        */
                        hasWorkspace ? (
                          <Link className="btn btn--small" to="/arbejdsrum/hold">
                            Se foreningens hold
                          </Link>
                        ) : (
                          <Link className="btn btn--small" to="/medlemslogin">
                            Forbind din profil
                          </Link>
                        )
                      ) : null
                    }
                  >
                    {data.family.length === 0
                      ? hasWorkspace
                        ? 'Her står din egen families hold. Har du børn i foreningen, kan du forbinde din profil under Profil — ellers finder du foreningens hold under Administration.'
                        : 'Forbind din konto med foreningens medlemsregister for at se jeres hold.'
                      : ingenHoldTekst(
                          person === 'alle'
                            ? null
                            : // Samme korte navn som på chippen ovenfor. Står der
                              // «Laurits» på knappen, skal sætningen ikke svare
                              // med «Laurits Tolstrup Jørgensen».
                              (() => {
                                const valgt = data.family.find((f) => f.personKey === person);
                                return valgt
                                  ? shortName(valgt.name, data.family.map((x) => x.name))
                                  : null;
                              })(),
                        )}
                  </EmptyState>
                </div>
              ) : (
                <div className="stack">
                  {filtered.map((team) => (
                    <Link key={team.teamId} className="card card--link" to={`/hold/${team.slug}`}>
                      {/*
                        Holdkortets rækkefølge er den samme på hvert eneste
                        kort: afdeling, navn, alder, tid, sted, familie. Det
                        er dét, der gør en lang liste hurtig at scanne —
                        øjet skal kunne lære ét mønster og genbruge det.
                      */}
                      <div className="card__header">
                        <div>
                          <Badge
                            kind={departmentBadgeKind(team.department)}
                            icon={departmentIcon(team.department)}
                          >
                            {departmentLabel(team.department)}
                          </Badge>
                          <h2 className="mt-2">{team.name}</h2>
                          {/*
                            Aldersgruppen står kun, hvis den ikke allerede
                            er i navnet. «Lillespring 0.-2. klasse» efterfulgt
                            af «0.–2. klasse» er ikke to oplysninger — det er
                            den samme, sat to gange, og det får kortet til at
                            se maskinskrevet ud.
                          */}
                          {visAldersgruppe(team.name, team.ageGroup) ? (
                            <p className="small muted">{team.ageGroup}</p>
                          ) : null}
                        </div>
                        <Icon name="forward" size={18} className="muted" />
                      </div>
                      <div className="meta mt-3">
                        {scheduleText(team) ? (
                          <Meta icon="clock" label="Tid">
                            {scheduleText(team)}
                          </Meta>
                        ) : (
                          <Meta icon="season" label="Art">
                            Medlemskab · ingen faste træningstider
                          </Meta>
                        )}
                        {team.location ? (
                          <Meta icon="place" label="Sted">
                            {team.location}
                          </Meta>
                        ) : null}
                        {team.members.length > 0 ? (
                          <Meta icon="family" label="Fra din familie">
                            {team.members.map((m) => m.name).join(', ')}
                          </Meta>
                        ) : null}
                      </div>
                      {team.awaitingTrainer || team.dataReview ? (
                        <div className="row mt-3">
                          {team.awaitingTrainer ? (
                            <Badge kind="warning" icon="trainer">
                              Afventer instruktør
                            </Badge>
                          ) : null}
                          {team.dataReview ? (
                            <Badge kind="warning" icon="warning">
                              Træningsdato skal bekræftes
                            </Badge>
                          ) : null}
                        </div>
                      ) : null}
                    </Link>
                  ))}
                </div>
              )}
            </>
          );
        }}
      </AsyncSection>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Holddetalje                                                         */
/* ------------------------------------------------------------------ */

interface TeamDetail {
  team: MyTeam;
  trainers: Array<{ name: string; role: string }>;
  trainersStatus: 'ok' | 'none_registered' | 'error';
  syncStatus: { status: string; lastSuccessAt: string | null; message: string };
  myMembers: Array<{ personKey: string; name: string }>;
  isStaff: boolean;
  participants: Array<{ memberId: string; name: string }> | null;
  counts: { participants: number; trainers: number; helpers: number; unresolved: number } | null;
  messagesNote: string;
}

export function TeamDetailPage({ slug }: { slug: string }): JSX.Element {
  const state = useAsync<TeamDetail>(() => api.get(`/api/teams/${encodeURIComponent(slug)}`), [slug]);

  return (
    <div className="stack">
      <AsyncSection state={state}>
        {(data) => (
          <>
            <nav className="crumbs" aria-label="Sti">
              <Link to="/mine-hold">
                <Icon name="back" size={16} />
                Mine hold
              </Link>
            </nav>

            <PageHead
              title={data.team.name}
              eyebrow={departmentLabel(data.team.department)}
              eyebrowIcon={departmentIcon(data.team.department)}
              sub={data.team.ageGroup || undefined}
            />

            {data.team.dataReview ? (
              <Notice kind="warning">
                <strong>Træningsdatoen skal bekræftes</strong>
                <p className="small mt-1">{data.team.dataReview}</p>
              </Notice>
            ) : null}

            {/*
              Tid, sted og første træning er det, siden findes for. De står
              som tre linjer med hvert sit pictogram frem for en
              definitionsliste med etiketter i én kolonne — det er både
              kortere og lettere at ramme med øjet på en telefon.
            */}
            {scheduleText(data.team) ? (
              <section className="section" aria-labelledby="traeningstid">
                <SectionHead title="Træningstid" icon="clock" id="traeningstid" />
                <div className="card">
                  <div className="meta">
                    <Meta icon="calendar" label="Ugedag og tid">
                      {scheduleText(data.team)}
                    </Meta>
                    <Meta icon="place" label="Sted">
                      {data.team.location || 'Ikke angivet'}
                    </Meta>
                    <Meta icon="season" label="Første træning">
                      {data.team.firstDate}
                    </Meta>
                  </div>
                </div>
              </section>
            ) : (
              /*
                Et medlemskab har ingen træningstider. Overskriften
                «Træningstid» med tre tomme linjer under ville se ud, som om
                appen manglede data. Her står der i stedet, hvad det ér.
              */
              <section className="section" aria-labelledby="medlemskab">
                <SectionHead title="Medlemskab" icon="season" id="medlemskab" />
                <div className="card">
                  <p className="small">
                    Det her er et medlemskab, ikke et hold med faste træningstider. Derfor står
                    der ingen træninger i kalenderen, og der kan ikke meldes afbud.
                  </p>
                </div>
              </section>
            )}

            <section className="section" aria-labelledby="instruktoerer">
              <SectionHead title="Instruktører" icon="trainer" id="instruktoerer" />
              {data.trainersStatus === 'error' ? (
                <Notice kind="warning">
                  Instruktørlisten kunne ikke hentes fra Conventus. Det betyder ikke, at holdet er
                  uden instruktør.
                </Notice>
              ) : data.trainersStatus === 'none_registered' ? (
                <div className="card">
                  <p className="small muted">
                    Der er ingen instruktør registreret på holdet i Conventus lige nu.
                    {data.team.awaitingTrainer
                      ? ' Holdet er oprettet som «afventer instruktør».'
                      : ''}
                  </p>
                </div>
              ) : (
                <div className="card">
                  <ul className="rows">
                    {data.trainers.map((t) => (
                      <li key={t.name} className="row row--between">
                        <span className="small">{t.name}</span>
                        {t.role === 'helper' ? <Badge kind="muted">Hjælper</Badge> : null}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </section>

            {data.myMembers.length > 0 ? (
              <section className="section" aria-labelledby="fra-familien">
                <SectionHead title="Fra din familie" icon="family" id="fra-familien" />
                <div className="card">
                  <p className="small">{data.myMembers.map((m) => m.name).join(', ')}</p>
                  <p className="mt-3">
                    <Link className="btn btn--secondary btn--small" to="/kalender">
                      <Icon name="calendar" size={17} />
                      Se træninger og meld afbud
                    </Link>
                  </p>
                </div>
              </section>
            ) : null}

            {data.isStaff && data.participants ? (
              <section className="section" aria-labelledby="deltagere">
                <SectionHead
                  title="Deltagere"
                  icon="teams"
                  id="deltagere"
                  action={<Badge>{antal(data.counts?.participants ?? 0, 'deltager', 'deltagere')}</Badge>}
                />
                <div className="card">
                  <p className="small muted">
                    Instruktører og hjælpere tælles ikke med som deltagere.
                  </p>
                  <ul className="rows mt-3">
                    {data.participants.map((p) => (
                      <li key={p.memberId} className="small">
                        {p.name}
                      </li>
                    ))}
                  </ul>
                  <p className="mt-3">
                    <Link
                      className="btn btn--secondary btn--small"
                      to={`/arbejdsrum/hold/${data.team.slug}`}
                    >
                      Åbn i administrationen
                    </Link>
                  </p>
                </div>
              </section>
            ) : null}

            <section className="section" aria-labelledby="holdbeskeder">
              <SectionHead title="Beskeder" icon="message" id="holdbeskeder" />
              <div className="card">
                {/* Holdsiden har bevidst ikke et selvstændigt beskedfeed (§7). */}
                <p className="small muted">{data.messagesNote}</p>
                <p className="mt-3">
                  <Link className="btn btn--secondary btn--small" to="/beskeder">
                    Åbn indbakken
                  </Link>
                </p>
              </div>
            </section>
          </>
        )}
      </AsyncSection>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Ingen adgang                                                        */
/* ------------------------------------------------------------------ */

export function NoAccessPage(): JSX.Element {
  return (
    <div className="stack">
      <div className="card">
        <EmptyState
          title="Du har ikke adgang til denne side"
          icon="roles"
          action={
            <Link className="btn" to="/">
              Tilbage til forsiden
            </Link>
          }
        >
          Siden kræver en adgang, din konto ikke har. Kontakt bestyrelsen, hvis du mener, det er en
          fejl.
        </EmptyState>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Frivillige                                                          */
/* ------------------------------------------------------------------ */

interface Shift {
  id: string;
  title: string;
  location: string;
  startsAt: string;
  capacity: number;
  signedUp: number;
  participants: string[];
  full: boolean;
  mySignup: boolean;
  eventId: string | null;
  eventTitle: string | null;
  eventCancelled: boolean;
}

export function VolunteersPage(): JSX.Element {
  const state = useAsync<{ shifts: Shift[]; canCreate: boolean; scopeNote: string }>(() =>
    api.get('/api/volunteer-shifts'),
  );
  const action = useAction();
  const [busyId, setBusyId] = useState<string | null>(null);

  const toggle = (shift: Shift): void => {
    setBusyId(shift.id);
    void action
      .run(async () => {
        if (shift.mySignup) {
          await api.del(`/api/volunteer-shifts/${shift.id}/signup`);
          action.setSuccess('Du er meldt fra opgaven.');
        } else {
          await api.post(`/api/volunteer-shifts/${shift.id}/signup`);
          action.setSuccess('Tak! Du er tilmeldt opgaven.');
        }
        state.reload();
      })
      .finally(() => setBusyId(null));
  };

  return (
    <div className="stack">
      <PageHead title="Frivillige opgaver" />
      {action.error ? <Notice kind="error">{action.error}</Notice> : null}
      {action.success ? <Notice kind="success">{action.success}</Notice> : null}

      <AsyncSection state={state}>
        {(data) => (
          <>
            <Notice kind="info">{data.scopeNote}</Notice>
            {data.shifts.length === 0 ? (
              <div className="card">
                <EmptyState title="Ingen opgaver lige nu" icon="volunteer">
                  Der er ikke oprettet kommende frivillige opgaver.
                </EmptyState>
              </div>
            ) : (
              data.shifts.map((shift) => (
                <article key={shift.id} className="card">
                  <div className="card__header">
                    <h2>{shift.title}</h2>
                    {shift.eventCancelled ? <Badge kind="warning">Aflyst</Badge> : null}
                    {shift.full && !shift.mySignup && !shift.eventCancelled ? (
                      <Badge kind="warning">Fyldt op</Badge>
                    ) : null}
                    {shift.mySignup ? (
                      <Badge kind="ok" icon="success">
                        Du er tilmeldt
                      </Badge>
                    ) : null}
                  </div>

                  {/*
                    HVAD hjælper man til? Det står lige under titlen, fordi
                    det er dét, der afgør, om man siger ja. «Oprydning» er en
                    opgave; «oprydning efter forårsopvisningen» er noget, man
                    kan se sig selv i.
                  */}
                  {shift.eventTitle ? (
                    <p className="small muted">
                      Ved <strong>{shift.eventTitle}</strong>
                    </p>
                  ) : null}

                  {shift.eventCancelled ? (
                    <div className="mt-2">
                      <Notice kind="warning">
                        Arrangementet er aflyst, så opgaven bliver ikke til noget. Den står her,
                        indtil den er overstået, så du kan se, hvad der skete.
                      </Notice>
                    </div>
                  ) : null}
                  <div className="meta mt-2">
                    <Meta icon="clock" label="Tidspunkt">
                      {formatDateTime(shift.startsAt)}
                    </Meta>
                    <Meta icon="place" label="Sted">
                      {shift.location}
                    </Meta>
                    <Meta icon="teams" label="Pladser">
                      {shift.signedUp} af {shift.capacity} pladser besat
                      {shift.participants.length > 0 ? `: ${shift.participants.join(', ')}` : ''}
                    </Meta>
                  </div>
                  <p className="mt-4">
                    <ActionButton
                      className={shift.mySignup ? 'btn btn--danger' : 'btn'}
                      pending={busyId === shift.id && action.pending}
                      /* Er arrangementet aflyst, kan man stadig melde FRA —
                         men ikke til. Serveren afviser uanset hvad. */
                      disabled={(shift.full || shift.eventCancelled) && !shift.mySignup}
                      onClick={() => toggle(shift)}
                    >
                      {shift.mySignup
                        ? 'Meld fra'
                        : shift.eventCancelled
                          ? 'Aflyst'
                          : shift.full
                            ? 'Fyldt op'
                            : 'Tilmeld mig'}
                    </ActionButton>
                  </p>
                </article>
              ))
            )}
          </>
        )}
      </AsyncSection>
    </div>
  );
}
